import { _buildQueryUrl, deleteRequest, get, postJSON, putJSON } from '../../../utils/Request';

export type DictionaryType = {
  id?: string;
  code?: string;
  name?: string;
  description?: string;
  visibility?: string;
  status?: string;
  version?: number;
};

export const DICTIONARY_STATUS_LABEL: Record<string, string> = {
  ACTIVE: '启用',
  DISABLED: '停用',
  DELETED: '已删除'
};

export const DICTIONARY_VISIBILITY_LABEL: Record<string, string> = {
  PUBLIC: '无需登录',
  ACCOUNT: '需登录'
};

export const DICTIONARY_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: '启用' },
  { value: 'DISABLED', label: '停用' },
  { value: 'DELETED', label: '已删除' }
] as const;

/** 查询字典类型列表。返回体原样交给页面。 */
export function getDictionaryTypes() {
  return get<DictionaryType[]>({ path: '/api/v1/dictionaries/types' });
}

/** 新增字典类型。不传 version。返回体原样交给页面。 */
export function createDictionaryType(body: {
  code: string;
  name: string;
  description?: string;
  visibility?: 'PUBLIC' | 'ACCOUNT';
  status?: 'ACTIVE' | 'DISABLED';
}) {
  return postJSON<DictionaryType>({
    path: '/api/v1/dictionaries/types',
    params: body
  });
}

/** 修改字典类型。必须传入当前 version，code 保持原值。返回体原样交给页面。 */
export function updateDictionaryType(
  typeId: string,
  body: {
    code: string;
    name: string;
    description?: string;
    visibility?: 'PUBLIC' | 'ACCOUNT';
    status?: 'ACTIVE' | 'DISABLED';
    version: number;
  }
) {
  return putJSON<DictionaryType>({
    path: `/api/v1/dictionaries/types/${encodeURIComponent(typeId)}`,
    params: body
  });
}

/** 删除字典类型。version 通过查询参数传入，取类型自身的版本。返回体原样交给页面。 */
export function deleteDictionaryType(typeId: string, version: number) {
  return deleteRequest<null>({
    path: _buildQueryUrl(`/api/v1/dictionaries/types/${encodeURIComponent(typeId)}`, { version })
  });
}

export type DictionaryItem = {
  id?: string;
  typeId?: string;
  value?: string;
  label?: string;
  description?: string;
  sortOrder?: number;
  status?: string;
  version?: number;
};

/** 查询指定字典类型下的字典项。返回体原样交给页面。 */
export function getDictionaryItems(typeId: string) {
  return get<DictionaryItem[]>({
    path: `/api/v1/dictionaries/types/${encodeURIComponent(typeId)}/items`
  });
}

/** 新增一条字典项。不传 version。返回体原样交给页面。 */
export function createDictionaryItem(
  typeId: string,
  body: {
    value: string;
    label: string;
    description?: string;
    sortOrder?: number;
    status?: 'ACTIVE' | 'DISABLED';
  }
) {
  return postJSON<DictionaryItem>({
    path: `/api/v1/dictionaries/types/${encodeURIComponent(typeId)}/items`,
    params: body
  });
}

/** 修改字典项。必须传入当前 version，value 保持原值。返回体原样交给页面。 */
export function updateDictionaryItem(
  typeId: string,
  itemId: string,
  body: {
    value: string;
    label: string;
    description?: string;
    sortOrder?: number;
    status?: 'ACTIVE' | 'DISABLED';
    version: number;
  }
) {
  return putJSON<DictionaryItem>({
    path: `/api/v1/dictionaries/types/${encodeURIComponent(typeId)}/items/${encodeURIComponent(itemId)}`,
    params: body
  });
}

/** 删除字典项。version 通过查询参数传入，取条目自身的版本。返回体原样交给页面。 */
export function deleteDictionaryItem(typeId: string, itemId: string, version: number) {
  return deleteRequest<null>({
    path: _buildQueryUrl(
      `/api/v1/dictionaries/types/${encodeURIComponent(typeId)}/items/${encodeURIComponent(itemId)}`,
      { version }
    )
  });
}

export type DictStatus = 'normal' | 'disabled';

export type DictItem = {
  id: string;
  label: string;
  value: string;
  sort: number;
  status: DictStatus;
  remark: string;
  createdAt: string;
  version?: number;
};

export type DictGroup = {
  id: string;
  name: string;
  type: string;
  status: DictStatus;
  remark: string;
  createdAt: string;
  items: DictItem[];
};

export const DICT_STATUS_LABEL: Record<DictStatus, string> = {
  normal: '正常',
  disabled: '停用'
};

export const DICT_STATUS_OPTIONS = [
  { value: 'normal', label: '正常' },
  { value: 'disabled', label: '停用' }
] as const;

function nowOffset(days: number, hour = 10) {
  const d = new Date('2026-06-12T10:00:00');
  d.setDate(d.getDate() + days);
  d.setHours(hour, 20, 11);
  return d.toISOString().slice(0, 19).replace('T', ' ');
}

export const adminDictGroups: DictGroup[] = [
  {
    id: 'dict-gender',
    name: '性别',
    type: 'gender',
    status: 'normal',
    remark: '用户性别枚举',
    createdAt: nowOffset(0),
    items: [
      {
        id: 'di-g-1',
        label: '男',
        value: '0',
        sort: 0,
        status: 'normal',
        remark: '',
        createdAt: nowOffset(0, 10)
      },
      {
        id: 'di-g-2',
        label: '女',
        value: '1',
        sort: 1,
        status: 'normal',
        remark: '',
        createdAt: nowOffset(0, 10)
      }
    ]
  },
  {
    id: 'dict-perks',
    name: '权益类型',
    type: 'perks_type',
    status: 'normal',
    remark: '单独权益分类',
    createdAt: nowOffset(1),
    items: [
      {
        id: 'di-p-1',
        label: '店铺',
        value: 'SHOP',
        sort: 0,
        status: 'normal',
        remark: '',
        createdAt: nowOffset(1, 11)
      },
      {
        id: 'di-p-2',
        label: '邮件',
        value: 'EMAIL',
        sort: 1,
        status: 'normal',
        remark: '',
        createdAt: nowOffset(1, 11)
      }
    ]
  },
  {
    id: 'dict-coupon',
    name: '优惠券使用金额最高占比',
    type: 'coupon_amount_proportion',
    status: 'normal',
    remark: '优惠券功能',
    createdAt: '2025-11-28 14:15:59',
    items: [
      {
        id: 'di-c-1',
        label: '默认占比',
        value: '0.3',
        sort: 0,
        status: 'normal',
        remark: '30%',
        createdAt: '2025-11-28 14:16:20'
      }
    ]
  },
  {
    id: 'dict-order-pay',
    name: '订单支付',
    type: 'order_pay',
    status: 'normal',
    remark: '订单支付',
    createdAt: '2025-11-28 14:14:54',
    items: [
      {
        id: 'di-o-1',
        label: '待支付',
        value: 'PENDING',
        sort: 0,
        status: 'normal',
        remark: '',
        createdAt: '2025-11-28 14:15:01'
      },
      {
        id: 'di-o-2',
        label: '已支付',
        value: 'PAID',
        sort: 1,
        status: 'normal',
        remark: '',
        createdAt: '2025-11-28 14:15:10'
      },
      {
        id: 'di-o-3',
        label: '已关闭',
        value: 'CLOSED',
        sort: 2,
        status: 'disabled',
        remark: '历史状态',
        createdAt: '2025-11-28 14:15:18'
      }
    ]
  },
  {
    id: 'dict-wechat',
    name: '微信支付回调',
    type: 'wechat_pay',
    status: 'normal',
    remark: '微信支付',
    createdAt: '2025-11-28 14:13:56',
    items: []
  },
  {
    id: 'dict-base-plan',
    name: '基础套餐配置',
    type: 'base_plan',
    status: 'normal',
    remark: '基础套餐配置',
    createdAt: '2025-09-23 14:41:58',
    items: [
      {
        id: 'di-bp-1',
        label: '试用天数',
        value: '14',
        sort: 0,
        status: 'normal',
        remark: '',
        createdAt: '2025-09-23 14:42:10'
      }
    ]
  },
  {
    id: 'dict-anti-spider',
    name: '反爬虫IP白名单配置',
    type: 'anti_spider_ip_white_key',
    status: 'disabled',
    remark: '反爬虫IP白名单配置',
    createdAt: '2025-09-23 11:48:52',
    items: []
  }
];
